// Thread picker shared by both datatalk and RAG chat panels (mode distinguishes which
// via AnalysisConversationMode). On mount, loads existing conversations for
// (patientId, mode); if none exist yet, transparently creates a first one so the chat
// panel always has a conversationId to attach messages to. Switching patient/mode
// re-triggers this via the parent remounting with a new `mode`/`patientId` prop.
import { useEffect, useState } from "react"
import {
    AnalysisConversationMode,
    AnalysisConversationSummary,
    AnalysisMessageEntry,
    CreateAnalysisConversationSpecification,
    GetAnalysisConversationsSpecification,
    GetAnalysisMessagesSpecification,
} from "pokal_api"

type Props = {
    patientId: string
    mode: AnalysisConversationMode
    onConversationChange: (
        conversationId: string,
        messages: AnalysisMessageEntry[]
    ) => void
    onOpenCompose?: () => void
}

export default function ConversationSelector({ patientId, mode, onConversationChange, onOpenCompose }: Props) {
    const [conversations, setConversations] = useState<AnalysisConversationSummary[]>([])
    const [activeId, setActiveId] = useState<string | null>(null)
    const [open, setOpen] = useState(false)
    const [busy, setBusy] = useState(false)

    useEffect(() => {
        let cancelled = false
        ;(async () => {
            setBusy(true)
            try {
                const list = (await GetAnalysisConversationsSpecification.request({
                    patientId,
                    mode,
                })) as AnalysisConversationSummary[]
                if (cancelled) return
                if (list.length === 0) {
                    const created = await CreateAnalysisConversationSpecification.request({
                        patientId,
                        mode,
                    })
                    if (cancelled) return
                    const summary: AnalysisConversationSummary = {
                        id: created.id,
                        title: created.title,
                        createdAt: created.createdAt,
                    }
                    setConversations([summary])
                    setActiveId(summary.id)
                    onConversationChange(summary.id, [])
                } else {
                    setConversations(list)
                    const first = list[0]
                    setActiveId(first.id)
                    const messages = (await GetAnalysisMessagesSpecification.request({
                        conversationId: first.id,
                    })) as AnalysisMessageEntry[]
                    if (cancelled) return
                    onConversationChange(first.id, messages)
                }
            } finally {
                if (!cancelled) setBusy(false)
            }
        })()
        return () => {
            cancelled = true
        }
    }, [patientId, mode])

    async function selectConversation(id: string) {
        setOpen(false)
        if (id === activeId) return
        setActiveId(id)
        setBusy(true)
        try {
            const messages = (await GetAnalysisMessagesSpecification.request({
                conversationId: id,
            })) as AnalysisMessageEntry[]
            onConversationChange(id, messages)
        } finally {
            setBusy(false)
        }
    }

    async function newConversation() {
        setOpen(false)
        setBusy(true)
        try {
            const created = await CreateAnalysisConversationSpecification.request({
                patientId,
                mode,
            })
            const summary: AnalysisConversationSummary = {
                id: created.id,
                title: created.title,
                createdAt: created.createdAt,
            }
            setConversations(prev => [summary, ...prev])
            setActiveId(summary.id)
            onConversationChange(summary.id, [])
        } finally {
            setBusy(false)
        }
    }

    const activeTitle = conversations.find(c => c.id === activeId)?.title ?? "—"

    return (
        <div className="flex items-center gap-2 px-3 py-1.5 border-b border-gray-100 flex-shrink-0 text-xs relative">
            <span className="text-gray-500">Gespräch:</span>
            <button
                type="button"
                disabled={busy || conversations.length === 0}
                onClick={() => setOpen(v => !v)}
                className="flex items-center gap-1 bg-white border border-gray-200 rounded px-2 py-0.5 text-gray-700 hover:border-gray-400 disabled:opacity-50"
            >
                <span>{activeTitle}</span>
                <span className="text-gray-400">▾</span>
            </button>
            <button
                type="button"
                disabled={busy}
                onClick={newConversation}
                className="text-teal-600 hover:text-teal-700 font-medium disabled:opacity-50"
                title="Neues Gespräch starten"
            >
                + Neu
            </button>
            {onOpenCompose && (
                <button
                    type="button"
                    onClick={onOpenCompose}
                    className="ml-auto flex items-center gap-1 rounded border border-teal-200 bg-teal-50 px-2 py-0.5 text-teal-700 hover:bg-teal-100 font-medium"
                    title="Patientennachricht verfassen"
                >
                    ✉ Nachricht
                </button>
            )}
            {open && conversations.length > 0 && (
                <div className="absolute left-3 top-full mt-1 z-20 bg-white border border-gray-200 rounded shadow-md min-w-[160px] max-h-64 overflow-y-auto">
                    {conversations.map(c => (
                        <button
                            key={c.id}
                            type="button"
                            onClick={() => selectConversation(c.id)}
                            className={`block w-full text-left px-3 py-1.5 hover:bg-gray-50 ${
                                c.id === activeId ? "bg-teal-50 text-teal-700 font-medium" : "text-gray-700"
                            }`}
                        >
                            {c.title}
                        </button>
                    ))}
                </div>
            )}
        </div>
    )
}
